import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { getGuardModuleState, getGuardEmergencyStatus } from '@/services/api.client';
import type { ShiftInfo } from '@/types';

export interface GuardContext {
  guardId: string;
  guardName: string;
  gateId: string;
  gateName: string;
  propertyId: string;
  propertyName: string;
  activeShift: ShiftInfo | null;
  isInvitesEnabled: boolean;
  isOnline: boolean;
  /** True when an emergency lockdown is active on the guard's property */
  isLockdown: boolean;
  /** Lockdown type string when active, e.g. 'fire', 'security_threat' */
  lockdownType: string | null;
  setActiveShift: (shift: ShiftInfo | null) => void;
}

// ─── Shift persistence via sessionStorage ────────────────────────────────────

const SHIFT_STORAGE_KEY = 'securegate-guard-active-shift';
const SHIFT_CHANGE_EVENT = 'securegate-shift-change';

// Module-level cache so readShift() returns the same object reference when the
// underlying sessionStorage value hasn't changed.  useSyncExternalStore requires
// getSnapshot to return a stable reference between calls — JSON.parse would
// always return a new object and cause React to throw "getSnapshot always returns
// a different value" (infinite re-render loop).
let _cachedShiftRaw: string | null = null;
let _cachedShift: ShiftInfo | null = null;

function readShift(): ShiftInfo | null {
  try {
    const raw = sessionStorage.getItem(SHIFT_STORAGE_KEY);
    if (!raw) {
      _cachedShiftRaw = null;
      _cachedShift = null;
      return null;
    }
    // Return cached object if the raw JSON string hasn't changed.
    if (raw === _cachedShiftRaw && _cachedShift !== null) {
      return _cachedShift;
    }
    _cachedShiftRaw = raw;
    _cachedShift = JSON.parse(raw) as ShiftInfo;
    return _cachedShift;
  } catch {
    return null;
  }
}

function writeShift(shift: ShiftInfo | null): void {
  try {
    if (shift) {
      sessionStorage.setItem(SHIFT_STORAGE_KEY, JSON.stringify(shift));
    } else {
      sessionStorage.removeItem(SHIFT_STORAGE_KEY);
    }
  } catch {
    // sessionStorage unavailable — ignore
  }
  // Notify all subscribers within this tab
  window.dispatchEvent(new CustomEvent(SHIFT_CHANGE_EVENT));
}

function subscribeShift(callback: () => void): () => void {
  window.addEventListener(SHIFT_CHANGE_EVENT, callback);
  return () => window.removeEventListener(SHIFT_CHANGE_EVENT, callback);
}

// ─── Online/offline tracking ─────────────────────────────────────────────────

function subscribeOnline(callback: () => void): () => void {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);
  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
}

function getOnlineSnapshot(): boolean {
  return navigator.onLine;
}

function getOnlineServerSnapshot(): boolean {
  return true;
}

// ─── Failed API detection (module-level) ─────────────────────────────────────

let _failedApiCount = 0;
let _apiOfflineListeners: Array<() => void> = [];

function notifyApiOfflineListeners() {
  _apiOfflineListeners.forEach((cb) => cb());
}

/**
 * Call when an API request fails with a network error.
 * After 2 consecutive failures, the hook reports offline even if navigator.onLine is true.
 */
export function reportGuardApiFailure(): void {
  _failedApiCount += 1;
  if (_failedApiCount >= 2) {
    notifyApiOfflineListeners();
  }
}

/**
 * Call when an API request succeeds, resetting the failure counter.
 */
export function reportGuardApiSuccess(): void {
  _failedApiCount = 0;
  notifyApiOfflineListeners();
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Central guard context hook providing identity, gate assignment, shift state,
 * module feature flags, and online/offline connectivity tracking.
 *
 * - Guard identity and property come from AuthContext user data
 * - Gate assignment comes from the active shift (persisted in sessionStorage)
 * - Module state (isInvitesEnabled) is fetched via getGuardModuleState on mount
 * - Online state uses navigator.onLine + window events + failed API detection
 */
export function useGuardContext(): GuardContext {
  const { user } = useAuth();

  // ── Guard identity from auth context ──
  const guardId = user?.id ?? '';
  const guardName = user?.fullName ?? '';
  const guardRole = user?.propertyRoles.find((r) => r.role === 'guard');
  const propertyId = guardRole?.propertyId ?? '';
  // propertyName is not available in MeProfile — derived from property config if needed
  const propertyName = '';

  // ── Active shift (shared via sessionStorage + custom event) ──
  const activeShift = useSyncExternalStore(subscribeShift, readShift, () => null);

  const setActiveShift = useCallback((shift: ShiftInfo | null) => {
    writeShift(shift);
  }, []);

  // Derive gate info from active shift
  const gateId = activeShift?.gateId ?? '';
  const gateName = activeShift?.gateName ?? '';

  // ── Module state (GUARD_WEB_INVITES) ──
  const { data: moduleState } = useQuery({
    queryKey: ['guard', 'module-state'],
    queryFn: getGuardModuleState,
    staleTime: 60_000, // matches Redis cache TTL of 60s
    retry: 1,
    refetchOnWindowFocus: true,
  });

  const isInvitesEnabled = moduleState?.invitesEnabled ?? false;

  // ── Emergency lockdown status ──
  // Polls every 30s — guards need to know about lockdowns promptly.
  // Only enabled once the guard has a known propertyId (i.e. is authenticated).
  const { data: emergencyStatus } = useQuery({
    queryKey: ['guard', 'emergency-status'],
    queryFn: getGuardEmergencyStatus,
    staleTime: 20_000,
    refetchInterval: 30_000,
    retry: 1,
    enabled: !!propertyId,
  });

  const isLockdown = emergencyStatus?.active ?? false;
  const lockdownType = emergencyStatus?.type ?? null;

  // ── Online/offline tracking ──
  const navigatorOnline = useSyncExternalStore(
    subscribeOnline,
    getOnlineSnapshot,
    getOnlineServerSnapshot,
  );

  // Combine navigator state with failed API detection
  const [apiOffline, setApiOffline] = useState(false);

  useEffect(() => {
    const listener = () => {
      setApiOffline(_failedApiCount >= 2);
    };
    _apiOfflineListeners.push(listener);
    return () => {
      _apiOfflineListeners = _apiOfflineListeners.filter((l) => l !== listener);
    };
  }, []);

  // Reset API offline state when navigator comes back online
  useEffect(() => {
    if (navigatorOnline) {
      _failedApiCount = 0;
      setApiOffline(false);
    }
  }, [navigatorOnline]);

  const isOnline = navigatorOnline && !apiOffline;

  return {
    guardId,
    guardName,
    gateId,
    gateName,
    propertyId,
    propertyName,
    activeShift,
    isInvitesEnabled,
    isOnline,
    isLockdown,
    lockdownType,
    setActiveShift,
  };
}
